import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Mail,
  CreditCard,
  Key,
  Shield,
  RefreshCw,
  ExternalLink,
  Clock,
  Zap,
  Flag
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import axios from "axios";
import { API } from "../../App";

const SystemHealthDashboard = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    fetchHealthData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchHealthData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchHealthData = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = { Authorization: `Bearer ${token}` };

      const [activityStats, featureFlags, webhookStatus] = await Promise.all([
        axios.get(`${API}/admin/activity-logs/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/feature-flags`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/system/webhook-status`, { headers }).catch(() => ({ data: null }))
      ]);

      setHealthData({
        activity: activityStats.data,
        flags: featureFlags.data?.feature_flags,
        webhooks: webhookStatus.data
      });
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to fetch health data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getHealthStatus = () => {
    if (!healthData?.activity) return "unknown";
    const errors24h = healthData.activity.errors_last_24h || 0;
    const critical24h = healthData.activity.critical_last_24h || 0;
    
    if (critical24h > 0) return "critical";
    if (errors24h > 5) return "warning";
    return "healthy";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "healthy": return "text-green-400";
      case "warning": return "text-yellow-400";
      case "critical": return "text-red-400";
      default: return "text-gray-400";
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case "healthy": return "bg-green-400/10";
      case "warning": return "bg-yellow-400/10";
      case "critical": return "bg-red-400/10";
      default: return "bg-gray-400/10";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "healthy": return <CheckCircle className="w-5 h-5 text-green-400" />;
      case "warning": return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case "critical": return <AlertCircle className="w-5 h-5 text-red-400" />;
      default: return <Activity className="w-5 h-5 text-gray-400" />;
    }
  };

  const healthStatus = getHealthStatus();

  // Count feature flags by status
  const flagStats = healthData?.flags ? {
    total: Object.keys(healthData.flags).length,
    enabled: Object.values(healthData.flags).filter(f => f.enabled).length,
    comingSoon: Object.values(healthData.flags).filter(f => f.coming_soon && !f.enabled).length
  } : { total: 0, enabled: 0, comingSoon: 0 };

  // Get category breakdown
  const categoryBreakdown = healthData?.activity?.by_category || {};
  const severityBreakdown = healthData?.activity?.by_severity || {};

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32">
            <div className="spinner" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border" data-testid="system-health-dashboard">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${getStatusBg(healthStatus)}`}>
            {getStatusIcon(healthStatus)}
          </div>
          <div>
            <CardTitle className="text-xl font-serif">System Health</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchHealthData}
            data-testid="refresh-health-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Link to="/admin/activity-log">
            <Button variant="outline" size="sm" className="gap-1">
              View Logs <ExternalLink className="w-3 h-3" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Overall Status Banner */}
        <div className={`p-4 rounded-lg ${getStatusBg(healthStatus)} border ${
          healthStatus === "critical" ? "border-red-500/30" : 
          healthStatus === "warning" ? "border-yellow-500/30" : "border-green-500/30"
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getStatusIcon(healthStatus)}
              <div>
                <p className={`font-medium ${getStatusColor(healthStatus)}`}>
                  {healthStatus === "healthy" && "All Systems Operational"}
                  {healthStatus === "warning" && "Some Issues Detected"}
                  {healthStatus === "critical" && "Critical Issues Require Attention"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {healthData?.activity?.errors_last_24h || 0} errors, {healthData?.activity?.critical_last_24h || 0} critical in last 24h
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Error Rate */}
          <div className="p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-orange-400" />
              <span className="text-xs text-muted-foreground">Errors (24h)</span>
            </div>
            <p className={`text-2xl font-medium ${
              (healthData?.activity?.errors_last_24h || 0) > 5 ? "text-orange-400" : "text-white"
            }`}>
              {healthData?.activity?.errors_last_24h || 0}
            </p>
          </div>

          {/* Total Events */}
          <div className="p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-muted-foreground">Total Events</span>
            </div>
            <p className="text-2xl font-medium">
              {healthData?.activity?.total_logs?.toLocaleString() || 0}
            </p>
          </div>

          {/* Payment Events */}
          <div className="p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-muted-foreground">Payment Events</span>
            </div>
            <p className="text-2xl font-medium">
              {categoryBreakdown.payment || 0}
            </p>
          </div>

          {/* Email Events */}
          <div className="p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-cyan-400" />
              <span className="text-xs text-muted-foreground">Email Events</span>
            </div>
            <p className="text-2xl font-medium">
              {categoryBreakdown.email || 0}
            </p>
          </div>
        </div>

        {/* Category Breakdown */}
        <div>
          <p className="text-sm font-medium mb-3">Event Categories</p>
          <div className="space-y-2">
            {[
              { key: "payment", label: "Payments", icon: <CreditCard className="w-4 h-4" />, color: "bg-emerald-400" },
              { key: "auth", label: "Authentication", icon: <Key className="w-4 h-4" />, color: "bg-purple-400" },
              { key: "subscription", label: "Subscriptions", icon: <RefreshCw className="w-4 h-4" />, color: "bg-amber-400" },
              { key: "email", label: "Email Delivery", icon: <Mail className="w-4 h-4" />, color: "bg-cyan-400" },
              { key: "system", label: "System", icon: <Shield className="w-4 h-4" />, color: "bg-slate-400" }
            ].map(({ key, label, icon, color }) => {
              const count = categoryBreakdown[key] || 0;
              const total = healthData?.activity?.total_logs || 1;
              const percentage = Math.round((count / total) * 100);
              
              return (
                <div key={key} className="flex items-center gap-3">
                  <div className="text-muted-foreground">{icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span>{label}</span>
                      <span className="text-muted-foreground">{count}</span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${color} rounded-full transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Feature Flags Summary */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium flex items-center gap-2">
              <Flag className="w-4 h-4" />
              Feature Flags
            </p>
            <Link to="/admin/feature-flags" className="text-xs text-primary hover:underline">
              Manage
            </Link>
          </div>
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <span className="text-sm text-muted-foreground">
                {flagStats.enabled} Enabled
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-yellow-400" />
              <span className="text-sm text-muted-foreground">
                {flagStats.comingSoon} Coming Soon
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-slate-400" />
              <span className="text-sm text-muted-foreground">
                {flagStats.total} Total
              </span>
            </div>
          </div>
        </div>

        {/* Webhook Status */}
        <div>
          <p className="text-sm font-medium mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Webhook Integrations
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-purple-400" />
                  <span className="text-sm">Stripe</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs text-green-400">Active</span>
                </div>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">SendGrid</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs text-green-400">Active</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Critical Events */}
        {(healthData?.activity?.critical_last_24h || 0) > 0 && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-sm font-medium text-red-400">Critical Events Detected</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {healthData.activity.critical_last_24h} critical event(s) in the last 24 hours. 
              <Link to="/admin/activity-log?severity=critical" className="text-red-400 hover:underline ml-1">
                View details →
              </Link>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SystemHealthDashboard;
